/**
 * In-memory admin API client backed by the mock fixtures in
 * `src/lib/api/mock-data/`.
 *
 * Why this exists:
 *   - Backend admin business endpoints are not built yet.
 *   - Features must use TanStack Query against typed promises that *match*
 *     the future contract shape (paginated reads, typed errors).
 *   - Swapping this module out for an OpenAPI-generated client later should
 *     be a one-import change at `admin-contract.ts`.
 *
 * Function signatures intentionally mirror what the real adapter will
 * expose. Do NOT add side effects, mutation handlers, or business rules
 * here — those belong in feature folders.
 */

import { ApiError } from "@/lib/api/api-error";
import type { AdminGiftRiskQueue } from "@/types/admin-gift-support";
import type {
  AdminUserSearchParams,
  AdminUserSearchResult,
  AdminUserListItem,
  AdminUserDetail,
  AdminSessionDetail,
  AdminTimelineEntry,
} from "@/types/admin-user";
import type {
  AdminMembershipDetail,
  AdminMembershipListRow,
} from "@/types/admin-membership";
import type {
  AdminHardwareListItem,
  AdminHardwareListParams,
  AdminHardwarePair,
} from "@/types/admin-hardware";
import type {
  AdminReclaimDetail,
  AdminReclaimListParams,
} from "@/types/admin-reclaim";
import type { AdminHardwareLostReportList } from "@/types/admin-hardware-lost-reports";
import type { AdminReplacementPrice } from "@/types/admin-hardware-replacement-price";
import type { AdminAppVersionFloors } from "@/types/admin-app-version";
import type { AdminPolicyVersions } from "@/types/admin-policy-version";
import type { AdminRiskSignals } from "@/types/admin-fraud";
import type {
  AdminDisputeListItem,
  AdminDisputeDetail,
  AdminDisputeEvidence,
  AdminDisputeSearchParams,
} from "@/types/admin-dispute";
import type {
  AdminAuditEvent,
  AdminAuditSearchParams,
} from "@/types/admin-audit";
import type { AdminPage } from "@/types/admin-api";
import type {
  AdminOperationsSummary,
  AdminNavigationMetricsResponse,
  AdminMembershipFunnel,
} from "@/types/admin-operations";
import type {
  AdminSupportActionPlaceholder,
  AdminSupportActionQueueRow,
} from "@/types/admin-support";

import {
  MOCK_USER_IDS,
  USER_LIST_ITEMS,
  USER_DETAILS,
  USER_SESSIONS,
  USER_TIMELINE,
  MOCK_MEMBERSHIPS,
  MOCK_HARDWARE,
  MOCK_HARDWARE_LIST,
  MOCK_RECLAIMS,
  MOCK_RECLAIMS_LIST,
  MOCK_RISK_SIGNALS,
  MOCK_DISPUTES,
  MOCK_DISPUTE_EVIDENCE,
  MOCK_AUDIT_EVENTS_BY_USER,
  MOCK_AUDIT_EVENTS_BY_ID,
  MOCK_AUDIT_EVENTS_ALL,
  MOCK_OPERATIONS_SUMMARY,
  MOCK_SUPPORT_PLACEHOLDERS,
  type MockUserId,
} from "@/lib/api/mock-data";
import { MOCK_SUPPORT_ACTIONS_QUEUE } from "@/lib/api/mock-data/support-queue.mock";
import { MOCK_LOST_REPORTS } from "@/lib/api/mock-data/lost-reports.mock";
import { MOCK_GIFTS } from "@/lib/api/mock-data/gifts.mock";
import { MOCK_GIFT_OBLIGATIONS } from "@/lib/api/mock-data/gift-obligations.mock";
import type { AdminGiftObligationsReport } from "@/types/admin-gift-obligations";
import type { AdminGiftListResponse } from "@/types/admin-gift";
import { MOCK_REPLACEMENT_PRICE } from "@/lib/api/mock-data/replacement-price.mock";
import { MOCK_APP_VERSION_FLOORS } from "@/lib/api/mock-data/app-version-floor.mock";
import {
  MOCK_SHIPMENTS_LIST,
  MOCK_MEMBERSHIP_NOTICES_SUMMARY,
  MOCK_MEMBERSHIP_NOTICES_FAILED,
  MOCK_MEMBERSHIP_NOTICES_DUE_PENDING,
  MOCK_OUTBOX_HEALTH,
  MOCK_EASYPOST_WEBHOOK_HEALTH,
  MOCK_EASYPOST_WEBHOOK_EVENTS,
} from "@/lib/api/mock-data/fulfillment.mock";
import type {
  AdminShipmentListItem,
  AdminShipmentListParams,
} from "@/types/admin-shipment";
import type {
  AdminMembershipNoticeSummary,
  AdminMembershipNoticeRow,
  AdminMembershipNoticeListParams,
} from "@/types/admin-notice";
import type {
  AdminOutboxHealth,
  AdminEasypostWebhookHealth,
  AdminWebhookEventRow,
  AdminWebhookEventListParams,
} from "@/types/admin-webhook-health";
import type { AdminBillingRetry } from "@/types/admin-demo-controls";
import { MOCK_BILLING_RETRY } from "@/lib/api/mock-data/billing-retry.mock";
import type { AdminPaymentHistory } from "@/types/admin-payments";
import { MOCK_PAYMENTS } from "@/lib/api/mock-data/payments.mock";

const DEFAULT_LATENCY_MS = 60;

function delay(ms = DEFAULT_LATENCY_MS): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function notFound(resource: string, id: string): ApiError {
  return new ApiError({
    code: "not_found",
    status: 404,
    message: `${resource} not found: ${id}`,
  });
}

function isMockUserId(id: string): id is MockUserId {
  return (MOCK_USER_IDS as readonly string[]).includes(id);
}

function paginate<T>(
  items: T[],
  page: number,
  pageSize: number,
): AdminPage<T> {
  const start = (page - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    total: items.length,
    page,
    pageSize,
  };
}

function semverLessThan(a: string, b: string): boolean {
  const pa = a.split(".").map((n) => parseInt(n, 10));
  const pb = b.split(".").map((n) => parseInt(n, 10));
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x < y;
  }
  return false;
}

function matchUser(
  item: AdminUserListItem,
  params: AdminUserSearchParams,
): boolean {
  if (
    params.query &&
    !`${item.email} ${item.displayName}`
      .toLowerCase()
      .includes(params.query.toLowerCase())
  ) {
    return false;
  }
  if (
    params.membershipState &&
    item.membershipState !== params.membershipState
  ) {
    return false;
  }
  if (params.countryCode && item.countryCode !== params.countryCode) {
    return false;
  }
  if (params.purchasePath && item.path !== params.purchasePath) {
    return false;
  }
  if (params.riskLevel) {
    const detail = USER_DETAILS[item.userId as MockUserId];
    if (!detail || detail.riskLevel !== params.riskLevel) return false;
  }
  if (params.accountStatus) {
    const detail = USER_DETAILS[item.userId as MockUserId];
    if (!detail || detail.profile.accountStatus !== params.accountStatus) {
      return false;
    }
  }
  return true;
}

export async function getAdminOperationsSummary(): Promise<AdminOperationsSummary> {
  await delay();
  return { ...MOCK_OPERATIONS_SUMMARY };
}

/**
 * Mock trial → paid funnel — the design-handoff demo numbers the pipeline card
 * used to hardcode. Keeps mock mode visually unchanged now that the card reads
 * this from the client instead of a constants file.
 */
export async function getAdminMembershipFunnel(): Promise<AdminMembershipFunnel> {
  await delay();
  return {
    signups: 188,
    delivered: 184,
    day21: 162,
    day31Attempted: 152,
    converted: 134,
    cardFailed: 11,
    returned: 7,
    conversionRate: 71.4,
    generatedAt: new Date(0).toISOString(),
    source: "mock",
  };
}

export async function getAdminNavigationMetrics(): Promise<AdminNavigationMetricsResponse> {
  await delay();
  const s = MOCK_OPERATIONS_SUMMARY;
  const membershipAttention =
    s.trialConversionFailures +
    s.trialChargeOffRisk +
    s.trialEndingSoon +
    s.paymentFailures;
  const overviewAlerts =
    membershipAttention +
    s.reclaimPending +
    s.fraudReviewQueue +
    s.disputesOpen +
    s.accountDeletionRequests +
    s.recentDangerousActions;
  return {
    overviewAlerts,
    usersTotal: MOCK_USER_IDS.length,
    membershipAttention,
    hardwareTotal: MOCK_HARDWARE_LIST.length,
    hardwareAttention: MOCK_HARDWARE_LIST.filter(
      (h) => (h as { status?: string }).status === "lost",
    ).length,
    reclaimsTotal: s.reclaimPending,
    fraudQueue: s.fraudReviewQueue,
    disputesOpen: s.disputesOpen,
    supportQueue:
      s.accountDeletionRequests + s.reclaimPending + s.paymentFailures,
    auditRecent: MOCK_AUDIT_EVENTS_ALL.length,
    generatedAt: new Date().toISOString(),
  };
}

export async function searchAdminUsers(
  params: AdminUserSearchParams = {},
): Promise<AdminUserSearchResult> {
  await delay();
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 25;
  const all = MOCK_USER_IDS.map((id) => USER_LIST_ITEMS[id]).filter((item) =>
    matchUser(item, params),
  );
  return paginate(all, page, pageSize);
}

export async function getAdminUserDetail(
  userId: string,
): Promise<AdminUserDetail> {
  await delay();
  if (!isMockUserId(userId)) throw notFound("user", userId);
  return USER_DETAILS[userId];
}

export async function getAdminUserTimeline(
  userId: string,
): Promise<AdminTimelineEntry[]> {
  await delay();
  if (!isMockUserId(userId)) throw notFound("user", userId);
  return USER_TIMELINE[userId];
}

export async function getAdminUserAuditEvents(
  userId: string,
): Promise<AdminAuditEvent[]> {
  await delay();
  if (!isMockUserId(userId)) throw notFound("user", userId);
  return MOCK_AUDIT_EVENTS_BY_USER[userId];
}

export async function getAdminUserSessions(
  userId: string,
): Promise<AdminSessionDetail[]> {
  await delay();
  if (!isMockUserId(userId)) throw notFound("user", userId);
  return USER_SESSIONS[userId];
}

export async function getAdminUserMembership(
  userId: string,
): Promise<AdminMembershipDetail> {
  await delay();
  if (!isMockUserId(userId)) throw notFound("user", userId);
  return MOCK_MEMBERSHIPS[userId];
}

/**
 * Mock variant of `GET /admin/users/{userId}/gifts` (I4). Previously this
 * read bypassed the mock/real chokepoint entirely and hard-coded
 * `{ items: [] }` for every user — indistinguishable from a real member with
 * no gifts, in the exact environment (local dev, mock-mode test runs) where
 * this screen is developed and demoed. Returns the same small stacked-gift
 * fixture regardless of user, mirroring `getAdminUserLostReports` — there is
 * no per-user gift scenario table yet.
 */
export async function getAdminUserGifts(
  _userId: string,
): Promise<AdminGiftListResponse> {
  await delay();
  return MOCK_GIFTS;
}

/**
 * Remaining gifted membership — the finance report. One fixture, deliberately
 * shaped so the screen's awkward cases (a stacked account, an account whose
 * furthest gift has no date, more undated gifts than dated ones) are visible in
 * demo mode instead of only in production. See the fixture's own comment.
 */
export async function getAdminGiftObligations(): Promise<AdminGiftObligationsReport> {
  await delay();
  return MOCK_GIFT_OBLIGATIONS;
}

/**
 * WS-F — one flagged redemption and one already-cleared one.
 *
 * Two rows, not one, so demo mode shows the case that matters most: a flag whose
 * reasons describe the BUYER, beside a recipient who may have done nothing but
 * accept a present. If the copy ever starts reading as an accusation, it should
 * be visible here rather than only in production.
 */
export async function getAdminGiftRiskQueue(
  params: { state?: string } = {},
): Promise<AdminGiftRiskQueue> {
  await delay();
  if ((params.state ?? "open") !== "open") {
    return { items: [], count: 0 };
  }
  return {
    items: [
      {
        reviewId: 2,
        orderNumber: "gift_demo_elevated",
        recipientUserId: 4021,
        severity: "elevated",
        reasons: ["card_multi_gift", "card_multi_account", "instant_redemption"],
        state: "open",
        flaggedAt: "2026-08-09T14:02:00Z",
      },
      {
        reviewId: 1,
        orderNumber: "gift_demo_review",
        recipientUserId: 3310,
        severity: "review",
        reasons: ["redemption_velocity", "buyer_money_reversal"],
        state: "open",
        flaggedAt: "2026-08-08T09:15:00Z",
      },
    ],
    count: 2,
  };
}

/**
 * Bulk membership list — mirrors the real GET /admin/memberships. Joins the
 * mock users with their membership fixtures so the dashboard hook can fetch
 * one page instead of fanning out per user.
 */
export async function listAdminMemberships(
  params: { page?: number; pageSize?: number } = {},
): Promise<AdminPage<AdminMembershipListRow>> {
  await delay();
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 100;
  const rows: AdminMembershipListRow[] = (MOCK_USER_IDS as readonly string[])
    .map((id): AdminMembershipListRow | null => {
      const user = USER_LIST_ITEMS[id as MockUserId];
      const m = MOCK_MEMBERSHIPS[id as MockUserId];
      // A memberships LIST row comes from a users⋈memberships join, so it always
      // has a state; a null one cannot form a row.
      if (!user || !m || m.state === null) return null;
      return {
        userId: id,
        email: user.email,
        displayName: user.displayName,
        membershipId: m.membershipId,
        state: m.state,
        path: m.path,
        futureBilling: m.futureBilling ?? null,
        trialStartedAt: m.trialStartedAt,
        trialEndsAt: m.trialEndsAt,
        commitmentEndsAt: m.commitmentEndsAt,
        nextBillingAt: m.nextBillingAt,
        pastDueSinceAt: m.pastDueSinceAt,
        amountMinor: m.amountMinor,
        currency: m.currency,
        failedPaymentCount: m.failedPaymentCount,
      };
    })
    .filter((r): r is AdminMembershipListRow => r !== null);
  return paginate(rows, page, pageSize);
}

export async function getAdminUserHardware(
  userId: string,
): Promise<AdminHardwarePair> {
  await delay();
  if (!isMockUserId(userId)) throw notFound("user", userId);
  return MOCK_HARDWARE[userId];
}

export async function listAdminHardware(
  params: AdminHardwareListParams = {},
): Promise<AdminPage<AdminHardwareListItem>> {
  await delay();
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 50;
  const filtered = MOCK_HARDWARE_LIST.filter((d) => {
    if (params.status && d.status !== params.status) return false;
    if (params.generation && d.hardwareGeneration !== params.generation) {
      return false;
    }
    if (params.firmwareBelowMin) {
      if (!d.firmwareVersion) return false;
      if (!semverLessThan(d.firmwareVersion, d.firmwareMinSupported)) {
        return false;
      }
    }
    if (params.query) {
      const q = params.query.toLowerCase();
      const hay = `${d.serial} ${d.orderId ?? ""} ${d.boundUserId ?? ""}`
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  return paginate(filtered, page, pageSize);
}

export async function getAdminUserReclaim(
  userId: string,
): Promise<AdminReclaimDetail | null> {
  await delay();
  if (!isMockUserId(userId)) throw notFound("user", userId);
  return MOCK_RECLAIMS[userId];
}

/**
 * Mock variant of `GET /admin/users/{userId}/hardware-lost-reports`. Returns
 * the same small fixture regardless of user — a read-only demo surface, not
 * a per-user scenario table like `MOCK_RECLAIMS`.
 */
export async function getAdminUserLostReports(
  _userId: string,
): Promise<AdminHardwareLostReportList> {
  await delay();
  return MOCK_LOST_REPORTS;
}

/**
 * Mock variant of `GET /admin/hardware/replacement-price`. ONE global price,
 * so there is no per-user scenario table — it returns the same fixture always.
 */
export async function getAdminReplacementPrice(): Promise<AdminReplacementPrice> {
  await delay();
  return MOCK_REPLACEMENT_PRICE;
}

/**
 * Mock variant of `GET /admin/app-version`. Always all three platforms, so
 * there is no per-user scenario table — it returns the same fixture always.
 */
/** WF-72. Mock mode ships the DORMANT registry — nothing in force, nobody
 *  prompted — because that is the real production state today. */
export async function getAdminPolicyVersions(): Promise<AdminPolicyVersions> {
  return {
    policies: {
      tos: null,
      privacy: null,
      commitment: null,
      dataUse: null,
      negativeOption: null,
    },
  };
}

export async function getAdminAppVersionFloors(): Promise<AdminAppVersionFloors> {
  await delay();
  return MOCK_APP_VERSION_FLOORS;
}

export async function listAdminReclaims(
  params: AdminReclaimListParams = {},
): Promise<AdminPage<AdminReclaimDetail>> {
  await delay();
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 50;
  const now = Date.now();
  const filtered = MOCK_RECLAIMS_LIST.filter((r) => {
    if (params.status && r.status !== params.status) return false;
    if (params.trigger && r.trigger !== params.trigger) return false;
    if (params.deadlinePassed) {
      if (!r.deadlineAt || Date.parse(r.deadlineAt) > now) return false;
    }
    if (params.query) {
      const q = params.query.toLowerCase();
      const hay = `${r.rmaId} ${r.serial} ${r.userId}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const rows: AdminReclaimDetail[] = filtered.map((r, index) => ({
    id: 9000 + index,
    rmaId: r.rmaId,
    userId: r.userId,
    serial: r.serial,
    trigger: r.trigger,
    status: r.status,
    labelIssuedAt: null,
    lastScanAt: r.lastScanAt ?? null,
    deadlineAt: r.deadlineAt ?? null,
    carrier: null,
    trackingNumber: null,
    flags: r.flags ?? [],
    refurbNotes: null,
    feeStatus: r.feeStatus ?? null,
    chargedOffAt: null,
    chargedOffAmountMinor: null,
  }));
  return paginate(rows, page, pageSize);
}

export async function getAdminUserRiskSignals(
  userId: string,
): Promise<AdminRiskSignals> {
  await delay();
  if (!isMockUserId(userId)) throw notFound("user", userId);
  return MOCK_RISK_SIGNALS[userId];
}

export async function listAdminDisputes(
  params: AdminDisputeSearchParams = {},
): Promise<AdminPage<AdminDisputeListItem>> {
  await delay();
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 25;
  const all = Object.values(MOCK_DISPUTES).filter((d) => {
    if (params.status && d.status !== params.status) return false;
    if (params.network && d.network !== params.network) return false;
    return true;
  });
  return paginate(all, page, pageSize);
}

export async function getAdminDisputeDetail(
  disputeId: string,
): Promise<AdminDisputeDetail> {
  await delay();
  const found = MOCK_DISPUTES[disputeId];
  if (!found) throw notFound("dispute", disputeId);
  return found;
}

export async function getAdminDisputeEvidence(
  disputeId: string,
): Promise<AdminDisputeEvidence> {
  await delay();
  const found = MOCK_DISPUTE_EVIDENCE[disputeId];
  if (!found) throw notFound("dispute_evidence", disputeId);
  return found;
}

export async function listAdminAuditLogs(
  params: AdminAuditSearchParams = {},
): Promise<AdminPage<AdminAuditEvent>> {
  await delay();
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 50;
  const filtered = MOCK_AUDIT_EVENTS_ALL.filter((e) => {
    if (params.targetType && e.targetType !== params.targetType) return false;
    if (params.actorAdminId && e.actorAdminId !== params.actorAdminId) {
      return false;
    }
    if (params.action && e.action !== params.action) return false;
    if (params.namespace && !e.action.startsWith(`${params.namespace}.`)) {
      return false;
    }
    if (params.targetId && e.targetId !== params.targetId) return false;
    if (params.severity && e.severity !== params.severity) return false;
    if (params.dangerousOnly && e.severity !== "warn" && e.severity !== "crit") {
      return false;
    }
    if (
      params.query &&
      !`${e.action} ${e.targetId} ${e.reason ?? ""}`
        .toLowerCase()
        .includes(params.query.toLowerCase())
    ) {
      return false;
    }
    if (params.fromDate && e.occurredAt < params.fromDate) return false;
    if (params.toDate && e.occurredAt > params.toDate) return false;
    return true;
  });
  return paginate(filtered, page, pageSize);
}

export async function getAdminAuditEvent(
  eventId: string,
): Promise<AdminAuditEvent> {
  await delay();
  const found = MOCK_AUDIT_EVENTS_BY_ID[eventId];
  if (!found) throw notFound("audit_event", eventId);
  return found;
}

export async function listAdminSupportActionPlaceholders(): Promise<
  AdminSupportActionPlaceholder[]
> {
  await delay();
  const rows: AdminSupportActionPlaceholder[] = [];
  for (const [userId, actions] of Object.entries(MOCK_SUPPORT_PLACEHOLDERS)) {
    const user = USER_LIST_ITEMS[userId as MockUserId];
    if (!user) continue;
    for (const actionType of actions) {
      rows.push({
        userId,
        displayName: user.displayName,
        email: user.email,
        actionType,
      });
    }
  }
  return rows;
}

export interface AdminSupportActionQueueParams {
  actionType?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Mock variant of `GET /admin/support-actions/queue`. Wraps the same
 * placeholders fixture in the offset `AdminPage` envelope so feature
 * code can target the real shape in both modes.
 */
export async function listAdminSupportActionQueue(
  params: AdminSupportActionQueueParams = {},
): Promise<AdminPage<AdminSupportActionQueueRow>> {
  await delay();
  let all = [...MOCK_SUPPORT_ACTIONS_QUEUE];
  if (params.actionType) {
    all = all.filter((row) => row.actionType === params.actionType);
  }
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 25;
  const start = (page - 1) * pageSize;
  return {
    items: all.slice(start, start + pageSize),
    total: all.length,
    page,
    pageSize,
  };
}

/* ─── Fulfillment / notice list / webhook-health (L67B mock fixtures) ── */

export async function listAdminShipments(
  params: AdminShipmentListParams = {},
): Promise<AdminPage<AdminShipmentListItem>> {
  await delay();
  const filtered = MOCK_SHIPMENTS_LIST.filter((s) => {
    if (params.shipmentStatus && s.shipmentStatus !== params.shipmentStatus) {
      return false;
    }
    if (params.membershipState && s.membershipState !== params.membershipState) {
      return false;
    }
    if (params.query) {
      const q = params.query.toLowerCase();
      const hay =
        `${s.membershipId ?? ""} ${s.shipmentId ?? ""} ${s.fulfillmentOrderId ?? ""} ${s.trackingNumber ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  return paginate(filtered, params.page ?? 1, params.pageSize ?? 50);
}

export async function getAdminMembershipNoticeSummary(): Promise<AdminMembershipNoticeSummary> {
  await delay();
  return MOCK_MEMBERSHIP_NOTICES_SUMMARY;
}

/**
 * Per-user billing-retry projection (mock). Unknown users 404 (the panel maps
 * `not_found` → the "no active billing retry" empty state, same as `source:none`).
 */
export async function getAdminUserBillingRetry(
  userId: string,
): Promise<AdminBillingRetry> {
  await delay();
  if (!isMockUserId(userId)) throw notFound("Billing retry", userId);
  return MOCK_BILLING_RETRY[userId];
}

/**
 * Per-user payment history (mock, W65-3). Unknown users 404, like the gateway.
 */
export async function getAdminUserPayments(
  userId: string,
): Promise<AdminPaymentHistory> {
  await delay();
  if (!isMockUserId(userId)) throw notFound("user", userId);
  return MOCK_PAYMENTS[userId];
}

export async function listAdminMembershipNoticesFailed(
  params: AdminMembershipNoticeListParams = {},
): Promise<AdminPage<AdminMembershipNoticeRow>> {
  await delay();
  return paginate(
    MOCK_MEMBERSHIP_NOTICES_FAILED,
    params.page ?? 1,
    params.pageSize ?? 50,
  );
}

export async function listAdminMembershipNoticesDuePending(
  params: AdminMembershipNoticeListParams = {},
): Promise<AdminPage<AdminMembershipNoticeRow>> {
  await delay();
  return paginate(
    MOCK_MEMBERSHIP_NOTICES_DUE_PENDING,
    params.page ?? 1,
    params.pageSize ?? 50,
  );
}

export async function getAdminOutboxHealth(): Promise<AdminOutboxHealth> {
  await delay();
  return MOCK_OUTBOX_HEALTH;
}

export async function getAdminEasypostWebhookHealth(): Promise<AdminEasypostWebhookHealth> {
  await delay();
  return MOCK_EASYPOST_WEBHOOK_HEALTH;
}

export async function listAdminEasypostWebhookEvents(
  params: AdminWebhookEventListParams = {},
): Promise<AdminPage<AdminWebhookEventRow>> {
  await delay();
  const filtered = params.status
    ? MOCK_EASYPOST_WEBHOOK_EVENTS.filter((e) => e.status === params.status)
    : MOCK_EASYPOST_WEBHOOK_EVENTS;
  return paginate(filtered, params.page ?? 1, params.pageSize ?? 50);
}
